import React, { useLayoutEffect, useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import copy from 'copy-to-clipboard'
import styled from '@emotion/styled'
import ErrorBoundary from 'components/ErrorBoundary'
import featuresStrings from 'utils/strings/features-strings'
import { stringCompareKey } from 'utils/sort'
import { stylesIf, themeColor } from 'utils/styles'
import {
  Table,
  Link,
  Loading,
  Tooltip,
  Icon,
  Paragraph,
  FlexRow,
  Divider,
} from 'components/patterns'
import SelectMenu from 'components/patterns/Inputs/SelectMenu'
import { yup } from 'schemas/validation'
import { searchFastFeatureMetadata } from 'services/fastFeatures'
import { ReactComponent as BrandSvg } from './BrandFastFeatures.svg'

const Params = {
  current: 'current',
  outputType: 'outputType',
  switchType: 'switch',
  solenoidType: 'solenoid',
  ecuType: 'ecu',
}

const ChipProps = {
  20: {
    value: `20${featuresStrings.amps_a}`,
    label: `20${featuresStrings.amps}`,
  },
  5: { value: `5${featuresStrings.amps_a}`, label: `5${featuresStrings.amps}` },
  2: { value: `2${featuresStrings.amps_a}`, label: `2${featuresStrings.amps}` },
  ECP: {
    value: 'ECP',
    label: 'ECP',
  },
}

const EcuChipProps = {
  XMC3: { value: 'XMC 3', label: 'XMC 3' },
  XMC4: { value: 'XMC 4', label: 'XMC 4' },
}

// Normalize API xmcModule ("XMC3") to UI display value ("XMC 3")
const ecuApiToUi = (apiValue) =>
  apiValue ? apiValue.replace(/^XMC(\d)$/, 'XMC $1') : 'XMC 3'

// Some environments return featureNumber without the CHK prefix and dash
// (e.g. "0007004230" instead of "CHK0007-004230"). Reconstruct the full format.
export const formatFeatureNumber = (raw) => {
  if (!raw) return raw
  if (/^CHK/i.test(raw)) return raw
  const m = raw.match(/^(\d{4})(\d{6})$/)
  return m ? `CHK${m[1]}-${m[2]}` : raw
}

// Extract solenoid display value from the first output with a solenoid field.
// solenoid: 1 → "Optional 1"
const getSolenoidDisplay = (feature) => {
  const output = (feature.outputs || []).find((o) => o.solenoid != null)
  if (!output) return null
  return `Optional ${output.solenoid}`
}

function StyledTooltip({ feature }) {
  const { featureId, featureNumber } = feature
  const displayNumber = formatFeatureNumber(featureNumber)
  const defaultLabel = featuresStrings.ff_copy_tooltip
  const copiedLabel = featuresStrings.ff_copied_tooltip
  const [label, updateLabel] = useState(defaultLabel)
  return (
    <>
      <StyledLink to={`/features/${featureId}`} target="_blank">
        {displayNumber}
      </StyledLink>
      <Tooltip
        title={label}
        placement="right"
        onClick={(e) => e.preventDefault()}
      >
        <StyledCopyButton
          onClick={() => {
            copy(displayNumber)
            updateLabel(copiedLabel)
          }}
          onMouseEnter={() => updateLabel(defaultLabel)}
        >
          <Icon icon="copy" />
          <span>{featuresStrings.copy}</span>
        </StyledCopyButton>
      </Tooltip>
    </>
  )
}

StyledTooltip.propTypes = {
  feature: PropTypes.object,
}

const StyledLink = styled(Link)`
  display: inline-block;
`

export const featureNumberSorter = (a, b) =>
  `${formatFeatureNumber(a.featureNumber) || ''}`.localeCompare(
    formatFeatureNumber(b.featureNumber) || ''
  )

const columns = [
  {
    title: featuresStrings.ff_column_feature_no,
    dataIndex: 'number',
    width: 210,
    render: (_, feature) => <StyledTooltip feature={feature} />,
    sorter: featureNumberSorter,
  },
  {
    title: featuresStrings.ff_column_description,
    dataIndex: 'description',
    render: (_, feature) => feature.description,
    sorter: stringCompareKey('description'),
  },
  {
    title: featuresStrings.ff_column_ecu,
    dataIndex: 'ecu',
    width: 80,
    render: (_, feature) => ecuApiToUi(feature.ecu),
    sorter: stringCompareKey('ecu'),
  },
  {
    title: featuresStrings.ff_column_feature_current,
    dataIndex: 'current',
    width: 90,
    render: (_, feature) => feature.fastFeatureFilter || 'N/A',
    sorter: stringCompareKey('fastFeatureFilter'),
  },
  {
    title: featuresStrings.ff_column_switch_name,
    dataIndex: 'switch-name',
    width: 170,
    render: (_, feature) => feature.inputDetails || 'N/A',
    sorter: stringCompareKey('inputDetails'),
  },
  {
    title: featuresStrings.ff_column_switch_part_no,
    dataIndex: 'switch-partno',
    width: 120,
    render: (_, feature) => feature.switchPn || 'N/A',
    sorter: stringCompareKey('switchPn'),
  },
  {
    title: featuresStrings.ff_column_solenoid,
    dataIndex: 'solenoid',
    width: 140,
    render: (_, feature) => getSolenoidDisplay(feature) || 'N/A',
    sorter: (a, b) => {
      const solA = getSolenoidDisplay(a) || 'N/A'
      const solB = getSolenoidDisplay(b) || 'N/A'
      if (solA === 'N/A' && solB === 'N/A') return 0
      if (solA === 'N/A') return 1
      if (solB === 'N/A') return -1
      return solA.localeCompare(solB)
    },
  },
]

// XMC3 has only 6 optional solenoids
const xmc3Solenoids = [
  'Optional 1',
  'Optional 2',
  'Optional 3',
  'Optional 4',
  'Optional 5',
  'Optional 6',
]

// Static switch options exported for use by AdvancedSearch filter.
// Mirrors the full set of TEM Custom Switches available in the system.
export const options = [
  { key: 'switch-all', value: 'all', title: featuresStrings.all_switches },
  ...Array.from({ length: 60 }, (_, i) => {
    const name = `TEM Custom Switch ${String(i).padStart(2, '0')}`
    return { key: `switch-${i + 1}`, value: name, title: name }
  }),
]

export const solenoidOptions = [
  { key: 'solenoid-all', value: 'all', title: featuresStrings.all_solenoids },
  { key: 'solenoid-na', value: 'N/A', title: 'N/A' },
  ...xmc3Solenoids.map((type, index) => ({
    key: `solenoid-${index + 1}`,
    value: type,
    title: type,
  })),
]

export const filteredData = (
  data,
  filters,
  type,
  solenoidType,
  outputTypeFilters,
  ecuFilters
) =>
  data.filter((feature) => {
    if (!feature.featureId) return false

    // Exclude features that have been toggled off as fast features
    if (feature.isFastFeature === false) return false

    // Exclude non-Active status features (e.g. Design, New, Returned, Obsolete)
    if (feature.status && feature.status !== 'Active') return false

    const featureEcu = ecuApiToUi(feature.ecu)
    if (ecuFilters.size > 0 && !ecuFilters.has(featureEcu)) return false

    const filterValue = feature.fastFeatureFilter
    if (filterValue === 'ECP') {
      if (!outputTypeFilters.has('ECP')) return false
    } else if (filterValue === 'SWI') {
      // Switch-indicator records have no dedicated chip — always include them
    } else if (filterValue) {
      if (!filters.has(filterValue)) return false
    }

    if (type !== 'all' && type !== feature.inputDetails) return false

    if (solenoidType !== 'all') {
      const sol = getSolenoidDisplay(feature) || 'N/A'
      if (solenoidType === 'N/A' && sol !== 'N/A') return false
      if (solenoidType !== 'N/A' && sol !== solenoidType) return false
    }

    return true
  })

export default function FastFeatures() {
  const history = useHistory()
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const filters = new Set([...params.getAll(Params.current)])
  const outputTypeFilters = new Set([...params.getAll(Params.outputType)])
  const ecuFilters = new Set([...params.getAll(Params.ecuType)])
  const selectedSwitchType = params.get(Params.switchType) || 'all'
  const selectedSolenoidType = params.get(Params.solenoidType) || 'all'

  const [allData, setAllData] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const pageSize = 200
        const MAX_PAGES = 20
        const seen = new Set()
        let allItems = []
        let offset = 0
        let hasMore = true
        let page = 0

        while (hasMore && page < MAX_PAGES) {
          // eslint-disable-next-line no-await-in-loop
          const response = await searchFastFeatureMetadata({
            limit: pageSize,
            offset,
            isFastFeature: true,
            featureStatus: 'Active',
          })
          const raw = response?.data ?? response
          const items = Array.isArray(raw) ? raw : raw?.items || []

          // Filter to only items not already seen — stops if the backend
          // returns duplicate data for repeated skip values
          const newItems = items.filter((item) => {
            const key = item.metaId ?? item.featureId
            if (seen.has(key)) return false
            seen.add(key)
            return true
          })

          // Always accumulate new items, including the last page
          allItems = allItems.concat(newItems)

          if (newItems.length === 0 || items.length < pageSize) {
            hasMore = false
          } else {
            offset += pageSize
          }
          page += 1
        }

        // Newest records (highest metaId) at the top
        const sorted = [...allItems].sort(
          (a, b) => (b.metaId ?? 0) - (a.metaId ?? 0)
        )
        setAllData(sorted)
      } catch (_err) {
        setAllData([])
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const switchOptions = [
    { key: 'switch-all', value: 'all', title: featuresStrings.all_switches },
    ...[...new Set(allData.map((f) => f.inputDetails).filter(Boolean)).values()]
      .sort()
      .map((name, index) => ({
        key: `switch-${index + 1}`,
        value: name,
        title: name,
      })),
  ]

  const dataSource = filteredData(
    allData,
    filters,
    selectedSwitchType,
    selectedSolenoidType,
    outputTypeFilters,
    ecuFilters
  )

  const noResultText = {
    emptyText: featuresStrings.ff_no_results_warning,
  }
  const chips = Object.values(ChipProps)
  const ecuChips = Object.values(EcuChipProps)

  const handleCurrentFilter = (value, active) => {
    if (value === 'ECP') {
      const updatedOutputTypeFilters = new Set(outputTypeFilters)
      if (active) {
        updatedOutputTypeFilters.add(value)
        params.set(Params.solenoidType, 'all')
      } else {
        updatedOutputTypeFilters.delete(value)
        params.set(Params.solenoidType, 'all')
      }
      params.delete(Params.outputType)
      updatedOutputTypeFilters.forEach((f) => {
        params.append(Params.outputType, f)
      })
    } else {
      const updatedFilters = new Set(filters)
      if (active) {
        updatedFilters.add(value)
      } else {
        updatedFilters.delete(value)
      }
      params.delete(Params.current)
      updatedFilters.forEach((f) => {
        params.append(Params.current, f)
      })
    }
    history.push({
      search: `?${params.toString()}`,
    })
  }

  const handleEcuFilter = (value, active) => {
    const updatedEcuFilters = new Set(ecuFilters)
    if (active) {
      updatedEcuFilters.add(value)
    } else {
      updatedEcuFilters.delete(value)
    }
    params.delete(Params.ecuType)
    updatedEcuFilters.forEach((f) => {
      params.append(Params.ecuType, f)
    })
    history.push({
      search: `?${params.toString()}`,
    })
  }

  const handleTypeFilter = (value) => {
    params.set(Params.switchType, value)
    history.push({
      search: `?${params.toString()}`,
    })
  }

  const handleSolenoidFilter = (value) => {
    params.set(Params.solenoidType, value)
    history.push({
      search: `?${params.toString()}`,
    })
  }

  useLayoutEffect(() => {
    if (filters.size === 0 && outputTypeFilters.size === 0) {
      const defaultParams = new URLSearchParams()
      defaultParams.append(Params.ecuType, EcuChipProps.XMC3.value)
      defaultParams.append(Params.ecuType, EcuChipProps.XMC4.value)
      defaultParams.append(Params.current, ChipProps[20].value)
      defaultParams.append(Params.current, ChipProps[5].value)
      defaultParams.append(Params.current, ChipProps[2].value)
      defaultParams.append(Params.outputType, ChipProps.ECP.value)
      history.replace({
        search: `?${defaultParams.toString()}`,
      })
    }
    // eslint-disable-next-line
  }, [])

  return (
    <ErrorBoundary>
      <StyledFastFeatures>
        <PageHeader>
          <StyledBrand />
        </PageHeader>
        <PageContent>
          <StyledParagraph>
            {featuresStrings.ff_fast_features_description}
          </StyledParagraph>
          <FlexRow
            align="top"
            style={{ height: '54px', margin: '16px 0', paddingLeft: '8px' }}
          >
            <StyledFilterDrawer>
              <StyledLabel>{featuresStrings.ff_filter_by_ecu}</StyledLabel>
              {ecuChips.map((c) => (
                <ChipButton
                  value={c.value}
                  active={ecuFilters.has(c.value)}
                  onToggle={handleEcuFilter}
                  key={c.value}
                >
                  {c.label}
                </ChipButton>
              ))}
            </StyledFilterDrawer>
            <Divider
              type="vertical"
              style={{ height: '100%', margin: '0 16px' }}
            />
            <div style={{ width: '224px' }}>
              <SelectMenu
                label={
                  <StyledLabel>
                    {featuresStrings.ff_filter_by_switch_no}
                  </StyledLabel>
                }
                name="switch-type"
                onChange={handleTypeFilter}
                options={switchOptions}
                value={selectedSwitchType}
                rules={yup.string().nullable()}
              />
            </div>
            <Divider
              type="vertical"
              style={{ height: '100%', margin: '0 16px' }}
            />
            <StyledFilterDrawer>
              <StyledLabel>
                {featuresStrings.ff_filter_by_output_type}
              </StyledLabel>
              {chips.map((c) => (
                <ChipButton
                  value={c.value}
                  active={
                    c.value === 'ECP'
                      ? outputTypeFilters.has(c.value)
                      : filters.has(c.value)
                  }
                  onToggle={handleCurrentFilter}
                  key={c.value}
                >
                  {c.label}
                </ChipButton>
              ))}
            </StyledFilterDrawer>
            <Divider
              type="vertical"
              style={{ height: '100%', margin: '0 16px' }}
            />
            <div style={{ width: '224px' }}>
              <SelectMenu
                label={
                  <StyledLabel>
                    {featuresStrings.ff_filter_by_solenoid}
                  </StyledLabel>
                }
                name="solenoid-type"
                onChange={handleSolenoidFilter}
                options={solenoidOptions}
                value={selectedSolenoidType}
                rules={yup.string().nullable()}
                disabled={!outputTypeFilters.has('ECP')}
              />
            </div>
          </FlexRow>
          {loading ? (
            <Loading size="sm" />
          ) : (
            <StyledTable
              {...{
                columns,
                dataSource,
                noResultText,
                rowKey: (feature, idx) =>
                  `${feature.metaId ?? feature.featureId ?? idx}`,
              }}
            />
          )}
        </PageContent>
      </StyledFastFeatures>
    </ErrorBoundary>
  )
}

FastFeatures.propTypes = {}

const StyledFastFeatures = styled.div(
  (props) => `
      margin: 0 -18px;
      background: ${themeColor('modalGrey', props)};
  `
)

const StyledBrand = styled(BrandSvg)(
  (props) => `
    width: 168px;
    height: 40px;
    vertical-align: middle;
  `
)

const StyledCopyButton = styled.button`
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  display: inline-block;
  vertical-align: top;
  margin: 0 0 0 8px;
  color: ${themeColor('bluePrimary')};
  &:hover {
    color: ${themeColor('blueAccent')};
  }
  .anticon-copy {
    color: inherit;
    display: inline-block;
    width: auto;
    height: auto;
    margin-right: 5px;
    > svg {
      display: block;
    }
  }
`
const PageHeader = styled.div`
  margin-bottom: 16px;
  padding: 16px 25px;
  background: white;
  border-bottom: 1px solid ${themeColor('navBorderBottomColor')};
`

const PageContent = styled.div`
  padding: 0 25px;
`

const StyledParagraph = styled(Paragraph)`
  color: ${themeColor('blackPrimary')};
`

const StyledFilterDrawer = styled.div`
  font-size: 12px;
  color: ${themeColor('blackSecondary')};
`

const StyledLabel = styled.div`
  margin-bottom: 5px;
`

const StyledTable = styled(Table)(
  () => `
    &&&& .ant-table-placeholder {
      height: auto;
      background: white;
      padding: 20px 0;
      color: gray;
    }

    width: 100%;

    .ant-table {
      overflow-x: hidden !important;
    }

    .ant-table-content {
      overflow-x: hidden !important;
    }

    .ant-table-body {
      overflow-x: hidden !important;
    }

    .ant-table-scroll {
      overflow-x: hidden !important;
    }
  `
)

const StyledChipButton = styled.button((props) => {
  const { active } = props
  const activeStyles = stylesIf(
    active,
    `
    background: ${themeColor('blueSecondary', props)};
    border: 1px solid ${themeColor('blueSecondary', props)};
    color: white;
    &:after {
      content: " \x00d7";
    }
    `
  )
  const inactiveStyles = stylesIf(
    !active,
    `
    background: ${themeColor('dark')};
    border: 1px solid gray;
    color: gray;
    &:after {
      content: ' +';
    }
    `
  )

  return `
    padding: 2px 6px;
    border-radius: 4px;
    margin: 0 4px;
    cursor: pointer;
    font-size: 12px;
    &:first-of-type {
      margin-left: 0;
    }
    &:after {
      position: relative;
      top: 1px;
      margin-left: 3px;
    }
    ${activeStyles}
    ${inactiveStyles}
  `
})

function ChipButton({ children, onToggle, value, active }) {
  const handleClick = (e) => {
    e.preventDefault()
    onToggle(value, !active)
  }
  return (
    <StyledChipButton type="button" onClick={handleClick} active={active}>
      {children}
    </StyledChipButton>
  )
}

ChipButton.propTypes = {
  onToggle: PropTypes.func,
  value: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
  active: PropTypes.bool,
}

ChipButton.defaultProps = {
  onToggle: () => {},
  active: false,
}
